<template>
  <div
    :class="[$style.participation, { [$style['participation--side']]: side }]"
  >
    <div :class="$style.media">
      <NuxtImg
        v-if="isImageContest"
        :src="item.image?.url"
        loading="lazy"
        alt=""
        :width="mediaAttributes.width"
        :height="mediaAttributes.height"
        :sizes="`sm:360px md:${mediaAttributes.width}px`"
        quality="80"
        format="auto"
      />
      <iframe
        v-else-if="isVideoContest"
        ref="vidPlayerDaily"
        :src="videoSrc"
        frameborder="0"
        loading="lazy"
      />
    </div>
    <div :class="$style.infos">
      <div :class="$style.infos__waper">
        <NuxtImg
          :src="item.user?.avatar?.url"
          :class="$style.infos__avatar"
          loading="lazy"
          quality="80"
          width="42"
          height="42"
          sizes="sm:42px tb:84px"
          fit="cover"
          format="auto"
        />
        <p :class="$style.infos__nickname">
          {{ item.user?.nickname }}
        </p>
      </div>
      <div :class="$style.infos__record">
        {{ formattedRecord }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ScRecord } from '~/types'
import {
  isImageContestKey,
  isRunContestKey,
  isRunSecondsContestKey,
  isScoreContestKey,
  isVideoContestKey,
} from '~/utils/provideKeys'

const props = withDefaults(
  defineProps<{
    item: ScRecord
    side?: boolean
    solo?: boolean
  }>(),
  {
    side: false,
    solo: false,
  },
)

const isImageContest = inject(isImageContestKey)
const isVideoContest = inject(isVideoContestKey)
const isScoreContest = inject(isScoreContestKey)
const isRunContest = inject(isRunContestKey)
const isRunSecondsContest = inject(isRunSecondsContestKey)

const videoSrc = computed(() => {
  if (!props.item?.video) return ''

  const videoData = extractVideoId(props.item.video)
  if (!videoData) return ''
  return buildEmbedUrl(videoData.source, videoData.id)
})

const formattedRecord = computed(() => {
  if (isScoreContest) {
    return formatNumber(props.item.score)
  }
  if (isRunContest) {
    return formatRun(props.item.run, !isRunSecondsContest)
  }

  return ''
})

const mediaAttributes = computed(() => {
  if (props.side && !props.solo) {
    return {
      width: 480,
      height: 270,
    }
  }

  return {
    width: 960,
    height: 540,
  }
})
</script>

<style lang="scss" module>
.participation {
  margin-bottom: 30px;

  @media (min-width: 992px) {
    margin-bottom: 50px;
  }

  &--side {
    max-width: 75vw;
    margin-bottom: 0;

    @media (min-width: 992px) {
      flex-grow: 1;
      max-width: none;
    }

    .infos {
      padding-top: 5px;
      padding-bottom: 5px;

      @media (min-width: 992px) {
        padding-top: 10px;
        padding-bottom: 10px;
      }
    }

    .media {
      @media (min-width: 992px) {
        margin-bottom: 20px;
      }
    }
  }
}

.media {
  margin-bottom: 10px;
  background-color: $sideGrey;

  @media (min-width: 992px) {
    margin-bottom: 30px;
  }

  img,
  // lite-youtube element
  .iframe-placeholder,
  // Other players element (Dailymotion, Vimeo)
  iframe {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @supports (aspect-ratio: 16 / 9) {
    aspect-ratio: 16/9;
  }

  @supports not (aspect-ratio: 16 / 9) {
    position: relative;
    width: 100%;
    padding-top: 56.25%; // 16x9
    overflow: hidden;

    img,
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      border: 0;
    }
  }
}

.infos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px 0;
  border-top: 1px solid rgba($black, 0.2);
  border-bottom: 1px solid rgba($black, 0.2);

  @media (min-width: 992px) {
    padding: 10px 0;
  }

  &__waper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
  }

  &__avatar {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    margin-right: 15px;
    overflow: hidden;
    border-radius: 50%;
    object-fit: cover;

    @media (min-width: 992px) {
      width: 42px;
      height: 42px;
    }
  }

  &__nickname {
    flex-grow: 1;
    height: 15px;
    font-family: SST;
    font-size: 12px;
    font-weight: 700;
    line-height: normal;
  }

  &__record {
    color: $blue;
    font-size: 14px;
    font-weight: 500;
  }
}
</style>
